import React, { useEffect, useState } from 'react';
import { ExtensionsService, Logger } from '_common/services';
import styles from './Template.module.scss';
import { Spinner } from '_common/components';

const thumbnailCache = new Map<string, string>();

type TemplateProps = {
  template: DocumentTemplate;
  selectTemplate: (value: DocumentTemplate) => void;
  active: boolean;
};

const Template = ({ template, selectTemplate, active }: TemplateProps) => {
  const [thumbnail, setThumbnail] = useState<string | undefined>(thumbnailCache.get(template.id));

  const onImageClick = () => {
    selectTemplate({ ...template, thumbnail });
  };

  useEffect(() => {
    if (template.id === '_no_template') return;

    const cached = thumbnailCache.get(template.id);
    if (cached) {
      setThumbnail(cached);
      return;
    }

    let cancelled = false;

    const loadTemplate = async () => {
      try {
        const service = new ExtensionsService();
        const response = await service.previewTemplate(template.id);
        if (cancelled) return;

        const url = URL.createObjectURL(response.data);
        thumbnailCache.set(template.id, url);
        setThumbnail(url);
      } catch (error) {
        Logger.captureException(error);
      }
    };

    loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [template.id]);

  const isLoading = template.id !== '_no_template' && thumbnail === undefined;

  const TemplateImage = () => {
    if (template.id === '_no_template') {
      return <div className={`${styles.img} ${styles.noTemplate}`} />;
    }

    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner size="medium" testId="template-gallery-loading" />
        </div>
      );
    }

    return <img className={styles.img} alt={template.name} src={thumbnail} />;
  };

  return (
    <div
      className={`${styles.root} ${active && styles.active}`}
      data-testid={`${template.name}-template`}
    >
      <div className={styles.img_container} onClick={onImageClick}>
        <TemplateImage />
      </div>
      <div className={styles.name}>{template.name}</div>
    </div>
  );
};

const Memo = React.memo(Template);
Memo.displayName = 'Template';
export default Memo;
